import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Tag, 
  Plus, 
  Eye, 
  Edit, 
  Trash2, 
  Percent, 
  DollarSign, 
  Gift, 
  ShoppingCart,
  Calendar,
  Clock,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { formatCurrency } from '../utils/formatters';

const TIPOS_OFERTA = [
  { value: 'AL_COSTO', label: 'Producto al Costo', icon: DollarSign, color: 'blue' },
  { value: 'PORCENTAJE_DESCUENTO', label: 'Descuento Porcentual', icon: Percent, color: 'green' },
  { value: 'DOS_POR_UNO', label: '2x1', icon: Gift, color: 'purple' },
  { value: 'SEGUNDA_MITAD_PRECIO', label: 'Segunda a Mitad de Precio', icon: ShoppingCart, color: 'orange' },
  { value: 'COMPRA_LLEVA_GRATIS', label: 'Compra X Lleva Y Gratis', icon: Gift, color: 'pink' },
  { value: 'DESCUENTO_CANTIDAD', label: 'Descuento por Cantidad', icon: Percent, color: 'indigo' },
  { value: 'COMBO', label: 'Combo (Múltiples Productos)', icon: ShoppingCart, color: 'teal' },
  { value: 'DESCUENTO_MONTO_TOTAL', label: 'Descuento por Monto Total', icon: DollarSign, color: 'red' }
];

const Ofertas = ({ user }) => {
  const [ofertas, setOfertas] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedOferta, setSelectedOferta] = useState(null);
  const [editMode, setEditMode] = useState(false);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    tipo: 'PORCENTAJE_DESCUENTO',
    fecha_inicio: '',
    fecha_fin: '',
    hora_inicio: '',
    hora_fin: '',
    producto_id: '',
    porcentaje_descuento: '',
    cantidad_requerida: '',
    producto_gratis_id: '',
    cantidad_gratis: 1,
    cantidad_minima: '',
    precio_combo: '',
    monto_minimo: '',
    productos_combo: []
  });

  const [productoCombo, setProductoCombo] = useState({ producto_id: '', cantidad: 1 });

  useEffect(() => {
    fetchOfertas();
    fetchProducts();
  }, []);

  const fetchOfertas = async () => {
    try {
      const response = await axios.get(`${API}/ofertas`);
      setOfertas(response.data);
    } catch (error) {
      toast.error('Error al cargar ofertas');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      tipo: 'PORCENTAJE_DESCUENTO',
      fecha_inicio: '',
      fecha_fin: '',
      hora_inicio: '',
      hora_fin: '',
      producto_id: '',
      porcentaje_descuento: '',
      cantidad_requerida: '',
      producto_gratis_id: '',
      cantidad_gratis: 1,
      cantidad_minima: '',
      precio_combo: '',
      monto_minimo: '',
      productos_combo: []
    });
    setEditMode(false);
    setSelectedOferta(null);
  };

  const handleOpenDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEditOferta = (oferta) => {
    setFormData({
      nombre: oferta.nombre,
      descripcion: oferta.descripcion || '',
      tipo: oferta.tipo,
      fecha_inicio: new Date(oferta.fecha_inicio).toISOString().slice(0, 16),
      fecha_fin: new Date(oferta.fecha_fin).toISOString().slice(0, 16),
      hora_inicio: oferta.hora_inicio || '',
      hora_fin: oferta.hora_fin || '',
      producto_id: oferta.producto_id || '',
      porcentaje_descuento: oferta.porcentaje_descuento || '',
      cantidad_requerida: oferta.cantidad_requerida || '',
      producto_gratis_id: oferta.producto_gratis_id || '',
      cantidad_gratis: oferta.cantidad_gratis || 1,
      cantidad_minima: oferta.cantidad_minima || '',
      precio_combo: oferta.precio_combo || '',
      monto_minimo: oferta.monto_minimo || '',
      productos_combo: oferta.productos_combo || []
    });
    setSelectedOferta(oferta);
    setEditMode(true);
    setShowDialog(true);
  };

  const handleSaveOferta = async () => {
    if (!formData.nombre || !formData.fecha_inicio || !formData.fecha_fin) {
      toast.error('Nombre, fecha de inicio y fecha de fin son requeridos');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        fecha_inicio: new Date(formData.fecha_inicio).toISOString(),
        fecha_fin: new Date(formData.fecha_fin).toISOString(),
        porcentaje_descuento: formData.porcentaje_descuento ? parseFloat(formData.porcentaje_descuento) : null,
        cantidad_requerida: formData.cantidad_requerida ? parseInt(formData.cantidad_requerida) : null,
        cantidad_gratis: formData.cantidad_gratis ? parseInt(formData.cantidad_gratis) : 1,
        cantidad_minima: formData.cantidad_minima ? parseInt(formData.cantidad_minima) : null,
        precio_combo: formData.precio_combo ? parseFloat(formData.precio_combo) : null,
        monto_minimo: formData.monto_minimo ? parseFloat(formData.monto_minimo) : null,
        hora_inicio: formData.hora_inicio || null,
        hora_fin: formData.hora_fin || null
      };

      if (editMode) {
        await axios.patch(`${API}/ofertas/${selectedOferta.id}`, payload);
        toast.success('Oferta actualizada');
      } else {
        await axios.post(`${API}/ofertas`, payload);
        toast.success('Oferta creada exitosamente');
      }

      setShowDialog(false);
      resetForm();
      fetchOfertas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar oferta');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEstado = async (oferta) => {
    try {
      const nuevoEstado = oferta.estado === 'ACTIVA' ? 'INACTIVA' : 'ACTIVA';
      await axios.patch(`${API}/ofertas/${oferta.id}`, { estado: nuevoEstado });
      toast.success(`Oferta ${nuevoEstado === 'ACTIVA' ? 'activada' : 'desactivada'}`);
      fetchOfertas();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleDeleteOferta = async (ofertaId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta oferta?')) return;

    try {
      await axios.delete(`${API}/ofertas/${ofertaId}`);
      toast.success('Oferta eliminada');
      fetchOfertas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar oferta');
    }
  };

  const viewOfertaDetail = async (ofertaId) => {
    try {
      const response = await axios.get(`${API}/ofertas/${ofertaId}`);
      setSelectedOferta(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Error al cargar detalle');
    }
  };

  const agregarProductoCombo = () => {
    if (!productoCombo.producto_id || !productoCombo.cantidad) {
      toast.error('Selecciona un producto y cantidad');
      return;
    }

    const producto = products.find(p => p.id === productoCombo.producto_id);
    if (!producto) return;

    const nuevoProducto = {
      producto_id: producto.id,
      producto_nombre: producto.nombre,
      cantidad: parseInt(productoCombo.cantidad)
    };

    setFormData({
      ...formData,
      productos_combo: [...formData.productos_combo, nuevoProducto]
    });

    setProductoCombo({ producto_id: '', cantidad: 1 });
  };

  const eliminarProductoCombo = (index) => {
    const nuevosProductos = [...formData.productos_combo];
    nuevosProductos.splice(index, 1);
    setFormData({ ...formData, productos_combo: nuevosProductos });
  };

  const getTipoInfo = (tipo) => {
    return TIPOS_OFERTA.find(t => t.value === tipo) || TIPOS_OFERTA[0];
  };

  const isOfertaVigente = (oferta) => {
    const now = new Date();
    const inicio = new Date(oferta.fecha_inicio);
    const fin = new Date(oferta.fecha_fin);
    return oferta.estado === 'ACTIVA' && now >= inicio && now <= fin;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Ofertas y Promociones</h1>
          <p className="text-slate-600">Gestiona las ofertas que se aplicarán en el POS</p>
        </div>
        <Button onClick={handleOpenDialog} className="bg-green-600 hover:bg-green-700">
          <Plus className="mr-2" size={18} />
          Nueva Oferta
        </Button>
      </div>

      {/* Lista de ofertas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ofertas.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="py-12 text-center text-slate-500">
              <Tag size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay ofertas registradas</p>
            </CardContent>
          </Card>
        ) : (
          ofertas.map((oferta) => {
            const tipoInfo = getTipoInfo(oferta.tipo);
            const TipoIcon = tipoInfo.icon;
            const vigente = isOfertaVigente(oferta);

            return (
              <Card key={oferta.id} className={`relative ${vigente ? 'border-green-300 bg-green-50' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <TipoIcon size={20} className={`text-${tipoInfo.color}-600`} />
                      <CardTitle className="text-lg">{oferta.nombre}</CardTitle>
                    </div>
                    <Badge variant={oferta.estado === 'ACTIVA' ? 'default' : 'secondary'}>
                      {oferta.estado}
                    </Badge>
                  </div>
                  <CardDescription>{oferta.descripcion}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Badge variant="outline" className={`bg-${tipoInfo.color}-100 text-${tipoInfo.color}-700`}>
                      {tipoInfo.label}
                    </Badge>
                  </div>

                  <div className="text-sm space-y-1">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar size={14} />
                      <span>
                        {new Date(oferta.fecha_inicio).toLocaleDateString()} - {new Date(oferta.fecha_fin).toLocaleDateString()}
                      </span>
                    </div>
                    {oferta.hora_inicio && oferta.hora_fin && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock size={14} />
                        <span>{oferta.hora_inicio} - {oferta.hora_fin}</span>
                      </div>
                    )}
                  </div>

                  {vigente && (
                    <div className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                      ✓ Vigente ahora
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewOfertaDetail(oferta.id)}
                      className="flex-1"
                    >
                      <Eye size={14} className="mr-1" />
                      Ver
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleEstado(oferta)}
                      className="flex-1"
                    >
                      {oferta.estado === 'ACTIVA' ? (
                        <><ToggleRight size={14} className="mr-1" />Desactivar</>
                      ) : (
                        <><ToggleLeft size={14} className="mr-1" />Activar</>
                      )}
                    </Button>
                    {(user.role === 'superadmin' || user.role === 'admin') && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditOferta(oferta)}
                        >
                          <Edit size={14} />
                        </Button>
                        {user.role === 'superadmin' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteOferta(oferta.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Dialog Crear/Editar Oferta */}
      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editMode ? 'Editar Oferta' : 'Nueva Oferta'}</DialogTitle>
            <DialogDescription>
              Configura los detalles de la oferta
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Información básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nombre">Nombre de la Oferta *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Ej: Oferta de Verano"
                />
              </div>
              <div>
                <Label htmlFor="tipo">Tipo de Oferta *</Label>
                <Select value={formData.tipo} onValueChange={(value) => setFormData({ ...formData, tipo: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_OFERTA.map((tipo) => (
                      <SelectItem key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Describe la oferta..."
                rows={2}
              />
            </div>

            {/* Vigencia */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fecha_inicio">Fecha y Hora de Inicio *</Label>
                <Input
                  id="fecha_inicio"
                  type="datetime-local"
                  value={formData.fecha_inicio}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="fecha_fin">Fecha y Hora de Fin *</Label>
                <Input
                  id="fecha_fin"
                  type="datetime-local"
                  value={formData.fecha_fin}
                  onChange={(e) => setFormData({ ...formData, fecha_fin: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="hora_inicio">Hora Inicio (Opcional)</Label>
                <Input
                  id="hora_inicio"
                  type="time"
                  value={formData.hora_inicio}
                  onChange={(e) => setFormData({ ...formData, hora_inicio: e.target.value })}
                  placeholder="HH:MM"
                />
                <p className="text-xs text-slate-500 mt-1">Deja vacío para todo el día</p>
              </div>
              <div>
                <Label htmlFor="hora_fin">Hora Fin (Opcional)</Label>
                <Input
                  id="hora_fin"
                  type="time"
                  value={formData.hora_fin}
                  onChange={(e) => setFormData({ ...formData, hora_fin: e.target.value })}
                  placeholder="HH:MM"
                />
              </div>
            </div>

            {/* Configuración según tipo de oferta */}
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Configuración de la Oferta</h3>

              {/* AL_COSTO */}
              {formData.tipo === 'AL_COSTO' && (
                <div>
                  <Label htmlFor="producto_al_costo">Producto *</Label>
                  <Select value={formData.producto_id} onValueChange={(value) => setFormData({ ...formData, producto_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nombre} - Costo: {formatCurrency(p.precio_compra)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">El producto se venderá a precio de costo</p>
                </div>
              )}

              {/* PORCENTAJE_DESCUENTO */}
              {formData.tipo === 'PORCENTAJE_DESCUENTO' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="producto_desc">Producto *</Label>
                    <Select value={formData.producto_id} onValueChange={(value) => setFormData({ ...formData, producto_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona el producto" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nombre} - {formatCurrency(p.precio_venta)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="porcentaje">Porcentaje de Descuento (%) *</Label>
                    <Input
                      id="porcentaje"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.porcentaje_descuento}
                      onChange={(e) => setFormData({ ...formData, porcentaje_descuento: e.target.value })}
                      placeholder="Ej: 20"
                    />
                  </div>
                </div>
              )}

              {/* DOS_POR_UNO */}
              {formData.tipo === 'DOS_POR_UNO' && (
                <div>
                  <Label htmlFor="producto_2x1">Producto *</Label>
                  <Select value={formData.producto_id} onValueChange={(value) => setFormData({ ...formData, producto_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nombre} - {formatCurrency(p.precio_venta)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Al comprar 2 unidades, paga solo 1</p>
                </div>
              )}

              {/* SEGUNDA_MITAD_PRECIO */}
              {formData.tipo === 'SEGUNDA_MITAD_PRECIO' && (
                <div>
                  <Label htmlFor="producto_mitad">Producto *</Label>
                  <Select value={formData.producto_id} onValueChange={(value) => setFormData({ ...formData, producto_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el producto" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.nombre} - {formatCurrency(p.precio_venta)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Al comprar 2, el segundo a mitad de precio</p>
                </div>
              )}

              {/* COMPRA_LLEVA_GRATIS */}
              {formData.tipo === 'COMPRA_LLEVA_GRATIS' && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="producto_compra">Producto a Comprar *</Label>
                      <Select value={formData.producto_id} onValueChange={(value) => setFormData({ ...formData, producto_id: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="cantidad_req">Cantidad Requerida *</Label>
                      <Input
                        id="cantidad_req"
                        type="number"
                        min="1"
                        value={formData.cantidad_requerida}
                        onChange={(e) => setFormData({ ...formData, cantidad_requerida: e.target.value })}
                        placeholder="Ej: 3"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="producto_gratis">Producto Gratis *</Label>
                      <Select value={formData.producto_gratis_id} onValueChange={(value) => setFormData({ ...formData, producto_gratis_id: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="cantidad_gratis">Cantidad Gratis *</Label>
                      <Input
                        id="cantidad_gratis"
                        type="number"
                        min="1"
                        value={formData.cantidad_gratis}
                        onChange={(e) => setFormData({ ...formData, cantidad_gratis: e.target.value })}
                        placeholder="Ej: 1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* DESCUENTO_CANTIDAD */}
              {formData.tipo === 'DESCUENTO_CANTIDAD' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="producto_cant">Producto *</Label>
                    <Select value={formData.producto_id} onValueChange={(value) => setFormData({ ...formData, producto_id: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.id} value={p.id}>
                            {p.nombre}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="cantidad_min">Cantidad Mínima *</Label>
                    <Input
                      id="cantidad_min"
                      type="number"
                      min="2"
                      value={formData.cantidad_minima}
                      onChange={(e) => setFormData({ ...formData, cantidad_minima: e.target.value })}
                      placeholder="Ej: 3"
                    />
                  </div>
                  <div>
                    <Label htmlFor="porcentaje_cant">% Descuento *</Label>
                    <Input
                      id="porcentaje_cant"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.porcentaje_descuento}
                      onChange={(e) => setFormData({ ...formData, porcentaje_descuento: e.target.value })}
                      placeholder="Ej: 10"
                    />
                  </div>
                </div>
              )}

              {/* COMBO */}
              {formData.tipo === 'COMBO' && (
                <div className="space-y-3">
                  <div className="border rounded-lg p-3 bg-slate-50">
                    <Label className="mb-2 block">Agregar Productos al Combo</Label>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <Select value={productoCombo.producto_id} onValueChange={(value) => setProductoCombo({ ...productoCombo, producto_id: value })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Producto" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.nombre}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="1"
                        value={productoCombo.cantidad}
                        onChange={(e) => setProductoCombo({ ...productoCombo, cantidad: e.target.value })}
                        placeholder="Cantidad"
                      />
                      <Button type="button" onClick={agregarProductoCombo} className="bg-blue-600">
                        <Plus size={16} className="mr-1" />
                        Agregar
                      </Button>
                    </div>
                  </div>

                  {formData.productos_combo.length > 0 && (
                    <div className="border rounded-lg p-3">
                      <Label className="mb-2 block">Productos en el Combo ({formData.productos_combo.length})</Label>
                      <div className="space-y-2">
                        {formData.productos_combo.map((p, index) => (
                          <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                            <span className="text-sm">
                              {p.cantidad}x {p.producto_nombre}
                            </span>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => eliminarProductoCombo(index)}
                              className="text-red-600"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="precio_combo">Precio del Combo *</Label>
                    <Input
                      id="precio_combo"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.precio_combo}
                      onChange={(e) => setFormData({ ...formData, precio_combo: e.target.value })}
                      placeholder="Ej: 500.00"
                    />
                  </div>
                </div>
              )}

              {/* DESCUENTO_MONTO_TOTAL */}
              {formData.tipo === 'DESCUENTO_MONTO_TOTAL' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="monto_min">Monto Mínimo de Compra (RD$) *</Label>
                    <Input
                      id="monto_min"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.monto_minimo}
                      onChange={(e) => setFormData({ ...formData, monto_minimo: e.target.value })}
                      placeholder="Ej: 1000.00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="porcentaje_monto">% Descuento *</Label>
                    <Input
                      id="porcentaje_monto"
                      type="number"
                      min="1"
                      max="100"
                      value={formData.porcentaje_descuento}
                      onChange={(e) => setFormData({ ...formData, porcentaje_descuento: e.target.value })}
                      placeholder="Ej: 5"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDialog(false);
                  resetForm();
                }}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveOferta}
                disabled={loading}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Guardando...' : (editMode ? 'Actualizar Oferta' : 'Crear Oferta')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Detalle de Oferta */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl">
          {selectedOferta && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedOferta.nombre}</DialogTitle>
                <DialogDescription>{selectedOferta.descripcion}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-slate-600">Tipo</Label>
                    <p className="font-semibold">{getTipoInfo(selectedOferta.tipo).label}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Estado</Label>
                    <Badge variant={selectedOferta.estado === 'ACTIVA' ? 'default' : 'secondary'}>
                      {selectedOferta.estado}
                    </Badge>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Fecha Inicio</Label>
                    <p className="font-semibold">{new Date(selectedOferta.fecha_inicio).toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-sm text-slate-600">Fecha Fin</Label>
                    <p className="font-semibold">{new Date(selectedOferta.fecha_fin).toLocaleString()}</p>
                  </div>
                  {selectedOferta.hora_inicio && selectedOferta.hora_fin && (
                    <>
                      <div>
                        <Label className="text-sm text-slate-600">Hora Inicio</Label>
                        <p className="font-semibold">{selectedOferta.hora_inicio}</p>
                      </div>
                      <div>
                        <Label className="text-sm text-slate-600">Hora Fin</Label>
                        <p className="font-semibold">{selectedOferta.hora_fin}</p>
                      </div>
                    </>
                  )}
                </div>

                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Detalles de la Oferta</h4>
                  {selectedOferta.producto_nombre && (
                    <p><span className="text-slate-600">Producto:</span> {selectedOferta.producto_nombre}</p>
                  )}
                  {selectedOferta.porcentaje_descuento && (
                    <p><span className="text-slate-600">Descuento:</span> {selectedOferta.porcentaje_descuento}%</p>
                  )}
                  {selectedOferta.producto_gratis_nombre && (
                    <p><span className="text-slate-600">Producto Gratis:</span> {selectedOferta.producto_gratis_nombre} (x{selectedOferta.cantidad_gratis})</p>
                  )}
                  {selectedOferta.cantidad_requerida && (
                    <p><span className="text-slate-600">Cantidad Requerida:</span> {selectedOferta.cantidad_requerida}</p>
                  )}
                  {selectedOferta.cantidad_minima && (
                    <p><span className="text-slate-600">Cantidad Mínima:</span> {selectedOferta.cantidad_minima}</p>
                  )}
                  {selectedOferta.precio_combo && (
                    <p><span className="text-slate-600">Precio Combo:</span> {formatCurrency(selectedOferta.precio_combo)}</p>
                  )}
                  {selectedOferta.monto_minimo && (
                    <p><span className="text-slate-600">Monto Mínimo:</span> {formatCurrency(selectedOferta.monto_minimo)}</p>
                  )}
                  {selectedOferta.productos_combo && selectedOferta.productos_combo.length > 0 && (
                    <div className="mt-2">
                      <Label className="text-sm text-slate-600">Productos del Combo:</Label>
                      <ul className="list-disc list-inside mt-1">
                        {selectedOferta.productos_combo.map((p, i) => (
                          <li key={i}>{p.cantidad}x {p.producto_nombre}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4 text-sm text-slate-600">
                  <p>Creado por: {selectedOferta.creado_por_nombre}</p>
                  <p>Fecha de creación: {new Date(selectedOferta.created_at).toLocaleString()}</p>
                </div>

                <Button onClick={() => setShowDetailDialog(false)} className="w-full">
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Ofertas;
